import {
  ActionAppendedField,
  AddNewEntry,
  TableCell,
  TableFooter,
  TableSelect,
  TableTextField,
} from "./Atom"
import PublishIcon from "@mui/icons-material/Publish"
import SaveIcon from "@mui/icons-material/Save"
import {
  clinicalTableFooter,
  columns,
  nonClinicalTableFooter,
} from "./TableConfigs"

import React from "react"
import { genericCreate, genericDelete, genericUpdate } from "./apiUtils"

import { toast } from "react-toastify"
import DeleteIcon from "@mui/icons-material/Delete"
import {
  actionType,
  forecastType,
  resourceBudgetType,
} from "../../../pages/ResourceBudget/types"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  updateForecastAppendedProvider,
  appendForecastProvider,
  updateForecastDirtyList,
  updateForecastNewEntriesList,
  deleteForecastProvider,
} from "../../../redux/slices/forecastSlice"
import {
  updateAppendedProvider,
  updateDirtyList,
  updateNewEntriesList,
  appendProvider,
  deleteProvider,
} from "../../../redux/slices/resourceBudgetSlice"
import { primaryTabs as pT } from "../../../pages/ResourceBudget/constants"
import { colors } from "../../../../styles/commonStyles"
import { prepareNumber } from "./validators"
import { permissions } from "../../../../config/constants"
import { formatNumber } from "../../../../helper/utils/formatting"

export const assignUnit = (data: any, col: string) => {
  const value = data[col]
  const safe = value !== null && value !== undefined && !isNaN(value)
  const currencyFields = [
    "amount_per_hour",
    "production",
    "compensation_amount",
    "wages_per_hour",
    "wages_per_month",
  ]

  const ignoreFields = ["role_title", "role_category", "resource_name"]
  if (safe) {
    if (col === "compensation_percent") {
      return formatNumber(value, "%")
    }
    if (ignoreFields.includes(col)) {
      return value
    }
    if (currencyFields.includes(col)) {
      return formatNumber(value, "$")
    }
    return formatNumber(value, "*")
  }

  // Return empty string for NaN, null, or undefined values instead of showing "NaN"
  return ""
}

export const strFormat = (str: string) => {
  if (!str) {
    return str
  }

  const chunks = str.split(" ")
  let formattedStr = ""
  chunks.forEach((chunk: string, index: number) => {
    formattedStr += chunk.charAt(0).toUpperCase() + chunk.slice(1)
    formattedStr += index < chunks.length - 1 ? " " : ""
  })
  return formattedStr
}

export const extractObject = (params: { data: any; index: string }) => {
  const { data, index } = params
  const indices = index?.split("-")

  let str = ""
  indices.forEach((key: string, index: number) => {
    str += data[key]
    str += index < indices.length - 1 ? " " : ""
  })

  return strFormat(str)
}
type colsProps = {
  state: forecastType | resourceBudgetType
  token: string | null
  isSlider: boolean
  onDeleteSuccess?: () => void
}
export const generateColsProps = ({ state, token, isSlider, onDeleteSuccess }: colsProps) => {
  const { primaryTabs, primaryIndex, secondaryIndex, dataIndex, aggregations } =
    state
  const { providerState, data, published, editState } = state
  const { newEntry, dirty } = editState
  const { resourceBudget } = state as resourceBudgetType

  const selectedoffice = useAppSelector(
    (store: any) => store.metric?.maevaSelectedPracticesNoAll?.[0],
  )
  const sliderSelectedOffice: forecastType = useAppSelector(
    (state: { forecast: any }) => state.forecast.filters.Offices.selected,
  )

  const officeFilter = isSlider ? sliderSelectedOffice : selectedoffice

  const { pinningCol, cols } = columns[dataIndex]
  const defaultEditMode = dataIndex === pT.nonClinical
  const dispatch = useAppDispatch()

  const allowed = useAppSelector((state) => {
    return state.rolesAndPerms.userPerms
  })
  const computedIndex = React.useMemo(
    () => primaryTabs[primaryIndex],
    [primaryIndex],
  )
  const canCreate = React.useMemo(() => {
    return published
      ? allowed.includes(permissions.BEFORE_PUBLISH_OPERATION) ||
      allowed.includes(permissions.AFTER_PUBLISH_OPERATION) // before publish
      : allowed.includes(permissions.FORECAST_OPERATION) // after publish
  }, [allowed, published])

  const canUpdate = React.useMemo(() => {
    return published
      ? allowed.includes(permissions.BEFORE_PUBLISH_OPERATION) ||
      allowed.includes(permissions.AFTER_PUBLISH_OPERATION) // before publish
      : allowed.includes(permissions.FORECAST_OPERATION) // after publish
  }, [allowed, published])

  const canDelete = React.useMemo(() => {
    return published
      ? allowed.includes(permissions.BEFORE_PUBLISH_OPERATION) ||
      allowed.includes(permissions.AFTER_PUBLISH_OPERATION) // before publish
      : allowed.includes(permissions.FORECAST_OPERATION) // after publish
  }, [allowed, published])

  const updateHandler = React.useCallback(genericUpdate[computedIndex], [
    computedIndex,
  ])
  const deleteHandler = React.useCallback(
    (params: any) => {
      // Wrap the genericDelete call to include onSuccess callback
      const baseDelete = genericDelete[computedIndex]
      return baseDelete({
        ...params,
        onSuccess: onDeleteSuccess,
      })
    },
    [computedIndex, onDeleteSuccess],
  )
  const createHandler = React.useCallback(genericCreate[computedIndex], [
    computedIndex,
  ])

  const stateCreateHandler = React.useCallback(
    resourceBudget ? appendProvider : appendForecastProvider,
    [resourceBudget],
  )
  const stateUpdateHandler = React.useCallback(
    (data: any) => {
      if (resourceBudget) {
        dispatch(updateAppendedProvider(data))
      } else {
        dispatch(updateForecastAppendedProvider(data))
      }
    },
    [resourceBudget],
  )
  const stateDirtyListHandler = React.useCallback(
    (data: any) => {
      if (resourceBudget) {
        dispatch(updateDirtyList(data))
      } else {
        dispatch(updateForecastDirtyList(data))
      }
    },
    [resourceBudget],
  )
  const stateNewEntryListHandler = React.useCallback(
    (data: any) => {
      if (resourceBudget) {
        dispatch(updateNewEntriesList(data))
      } else {
        dispatch(updateForecastNewEntriesList(data))
      }
    },
    [resourceBudget],
  )
  const stateDeleteHandler = React.useCallback(
    (data: any) => {
      if (resourceBudget) {
        dispatch(deleteProvider(data))
      } else {
        dispatch(deleteForecastProvider(data))
      }
    },
    [resourceBudget],
  )

  const checkStaffAndRole = React.useCallback((data: any) => {
    // For non-clinical, require all three: name, role_category, and role_title
    const isNonClinical = data?.primaryIndex === 1

    if (!data?.provider?.resource_name || data.provider.resource_name.trim() === "") {
      toast.error("Staff member should have a name")
      return false
    }

    if (isNonClinical) {
      // Non-clinical requires all three fields
      if (!data?.provider?.role_category || data.provider.role_category === "Select role") {
        toast.error("Staff member should have a role category assigned")
        return false
      }
      if (!data?.provider?.role_title || data.provider.role_title.trim() === "") {
        toast.error("Staff member should have a role title assigned")
        return false
      }
    } else {
      // Clinical tabs keep role requirement
      if (data?.provider?.role_category === "Select role") {
        toast.error(
          "Staff member should have a role category assigned to him/her",
        )
        return false
      }
    }
    return true
  }, [])
  const checkResourceName = React.useCallback((data: any) => {
    if (data?.provider?.resource_name === "Select Provider") {
      toast.error("Please select a provider first")
      return false
    }
    return true
  }, [])

  // actions generator
  const generateActions = React.useCallback(
    (row: any) => {
      // No need for refreshData - the setSaved callback will handle everything
      // For forecast (resourceBudget = false), we don't show row-level save buttons
      // All saves are done via "Save All" button at the top

      let actions: Partial<actionType | null>[] = [
        canCreate
          ? {
            enabled: newEntry && row.index === 1,
            apiCall: createHandler,
            setSaved: (data: any) => {
              // Call the state handler to turn off edit mode and update the row
              stateNewEntryListHandler({ id: row.index, newObject: data })
            },
            Icon: PublishIcon,
            test: defaultEditMode ? checkStaffAndRole : checkResourceName,
            hidden: defaultEditMode,
          }
          : null,
        // Only show Save button for resource budget, not for forecast
        canUpdate && resourceBudget
          ? {
            apiCall: updateHandler,
            setSaved: () => stateDirtyListHandler(row.index),
            Icon: SaveIcon,
            hidden: defaultEditMode,
          }
          : null,
        canDelete
          ? {
            apiCall: deleteHandler,
            setSaved: (id: number) => stateDeleteHandler(id),
            Icon: DeleteIcon,
            enabled: true,
            iconStyles: { color: colors.grey300 },
            alert: true,
          }
          : null,
      ]

      let allowedActions: actionType[] = []
      actions.forEach((action: Partial<actionType | null>) => {
        action &&
          allowedActions.push({
            enabled:
              (newEntry && row?.index === 1) || dirty.includes(row?.index),
            iconStyles: { color: colors.primaryBlue },
            payload: {
              provider: row?.original,
              token,
              office: officeFilter,
              year:
                (state as resourceBudgetType).selectedYear ||
                (state as forecastType).filters.Year,
              month:
                (state as resourceBudgetType).month ||
                (state as forecastType).filters.month,
              published,
              isBudget: resourceBudget,
              primaryIndex,
              officeFilter,
            },
            ...action,
          } as actionType)
      })
      return allowedActions
    },
    [
      published,
      defaultEditMode,
      newEntry,
      dirty,
      computedIndex,
      stateUpdateHandler,
      stateDirtyListHandler,
      stateNewEntryListHandler,
      stateDeleteHandler,
      canCreate,
      canUpdate,
      canDelete,
      resourceBudget,
      officeFilter,
      state,
    ],
  )

  const colsConfig = React.useMemo(() => {
    return cols.map((col: any) => {
      if (col["accessorKey"] === pinningCol) {
        return {
          ...col,
          Cell: ({ renderedCellValue, row, cell }: any) => {
            if (row.index > 0) {
              const actions = generateActions(row)
              return (
                <ActionAppendedField
                  cell={cell}
                  fieldValue={renderedCellValue}
                  referenceData={data}
                  index="firstname-lastname"
                  selectValues={
                    primaryIndex === 0
                      ? secondaryIndex === 0
                        ? providerState?.doctors || providerState?.data || []
                        : providerState?.hygienists || providerState?.data || []
                      : providerState?.data || []
                  }
                  newEntry={newEntry && row.index === 1}
                  key={row?.index}
                  actions={actions}
                  defaultEditMode={defaultEditMode}
                  updateHandler={stateUpdateHandler}
                />
              )
            } else {
              return renderedCellValue
            }
          },
          Footer: () => {
            return (
              <TableFooter
                col={col["accessorKey"]}
                renderValue={assignUnit(
                  aggregations[dataIndex].data,
                  col["accessorKey"],
                )}
              />
            )
          },
        }
      }
      if (col["accessorKey"] === "role_category") {
        return {
          ...col,
          Footer: () => {
            return (
              <TableFooter
                col={col["accessorKey"]}
                renderValue={assignUnit(
                  aggregations[dataIndex].data,
                  col["accessorKey"],
                )}
              />
            )
          },
          Cell: ({ renderedCellValue, row, cell }: any) => {
            if (row.index > 0) {
              // For non-clinical, use role categories from providerState.data
              // These are role category objects with role_category field
              const roleCategories = providerState?.data || []
              return (
                <TableSelect
                  cell={cell}
                  index="role_category"
                  selectValues={roleCategories}
                  dispatch={stateUpdateHandler}
                  label={"Role Category"}
                  isRoleCategory={true}
                />
              )
            } else {
              return renderedCellValue
            }
          },
        }
      }
      if (col["accessorKey"] === "role_title") {
        return {
          ...col,
          Footer: () => {
            return (
              <TableFooter
                col={col["accessorKey"]}
                renderValue={assignUnit(
                  aggregations[dataIndex].data,
                  col["accessorKey"],
                )}
              />
            )
          },
          Cell: ({ renderedCellValue, row, cell }: any) => {
            if (newEntry && row.index === 1) {
              return (
                <TableTextField
                  cell={cell}
                  dispatch={stateUpdateHandler}
                  label={"Role title"}
                  index={"role_title"}
                />
              )
            } else {
              return <TableCell renderValue={renderedCellValue} />
            }
          },
        }
      }
      return {
        ...col,
        Footer: () => {
          return (
            <TableFooter
              col={col["accessorKey"]}
              renderValue={assignUnit(
                aggregations[dataIndex].data,
                col["accessorKey"],
              )}
            />
          )
        },
      }
    })
  }, [
    dataIndex,
    cols,
    providerState,
    generateActions,
    aggregations,
    stateUpdateHandler,
    newEntry,
    published,
    primaryIndex,
  ])

  const enhancedData = React.useMemo(() => {
    console.log("🟠 [enhancedData] Input data:", {
      count: data?.length || 0,
      items: (data || []).map((d: any) => ({
        id: d?.id,
        provider_sk: d?.provider_sk,
        resource_name: d?.resource_name,
      })),
      primaryIndex,
      secondaryIndex,
    })
    const loadingProviders = providerState?.loading

    // Exclude providers already present in this tab
    const existingProviderSk = new Set(
      (data || [])
        .map((d: any) => d?.provider_sk)
        .filter((v: any) => v !== undefined && v !== null),
    )
    console.log(
      "🟠 [enhancedData] Existing provider_sk:",
      Array.from(existingProviderSk),
    )
    // For non-clinical tabs, there's no provider list requirement
    // So the "+Add" button should never be disabled based on provider availability
    const isNonClinical = primaryIndex === 1

    // Get the correct provider list based on current tab (only for clinical tabs)
    const currentProviderList =
      primaryIndex === 0
        ? secondaryIndex === 0
          ? providerState?.doctors || providerState?.data || []
          : providerState?.hygienists || providerState?.data || []
        : [] // Non-clinical doesn't use providers
    // Filter out providers with empty names AND already added providers (only for clinical)
    const availableProviders = isNonClinical
      ? [] // Non-clinical doesn't use providers
      : currentProviderList.filter((p: any) => {
        // Exclude if already added
        if (existingProviderSk.has(p?.provider_sk)) {
          return false
        }
        // Exclude if both firstname and lastname are empty/null/undefined
        const firstName = (p?.firstname || "").trim()
        const lastName = (p?.lastname || "").trim()
        // Include only if at least one name field has a value
        return firstName.length > 0 || lastName.length > 0
      })
    const hasAvailable = isNonClinical ? true : availableProviders.length > 0

    // Check if there are any dirty cells in the current table
    const hasDirtyCells = dirty.length > 0

    // For non-clinical: disable if there's already a new entry OR if there are dirty cells
    // For clinical: disable if newEntry, loadingProviders, no available providers, OR if there are dirty cells
    const disableAdd = isNonClinical
      ? newEntry || hasDirtyCells
      : newEntry || loadingProviders || !hasAvailable || hasDirtyCells
    const disabledReason = isNonClinical
      ? hasDirtyCells
        ? "Please save changes before adding a new resource"
        : undefined
      : loadingProviders
        ? "Loading providers..."
        : !hasAvailable
          ? "No available providers to add"
          : hasDirtyCells
            ? "Please save changes before adding a new resource"
            : undefined

    const result = [
      {
        [pinningCol]: (
          <AddNewEntry
            disable={disableAdd}
            disabledReason={disabledReason}
            appendHandler={stateCreateHandler}
          />
        ),
      },
      ...(data || []),
    ]
    console.log("🟠 [enhancedData] Final enhancedData:", {
      count: result.length,
      hasAddRow: true,
      dataRows: result.length - 1,
      items: result.slice(1).map((d: any, idx: number) => ({
        index: idx + 1,
        id: d?.id,
        provider_sk: d?.provider_sk,
        resource_name: d?.resource_name,
      })),
    })
    return result
  }, [
    data,
    stateCreateHandler,
    newEntry,
    dirty,
    providerState?.data,
    providerState?.loading,
    pinningCol,
  ])

  return { colsConfig, enhancedData }
}

// Helper function for consistent rounding to avoid floating point errors
const roundTo2Decimals = (num: number): number => {
  return Math.round(num * 100) / 100
}

const prepareForArithmetic = (cObj: any) => {
  const strFields = [
    "role_category",
    "role_title",
    "resource_name",
    "id",
    // Provider identity fields must remain strings
    "firstname",
    "lastname",
    "provider_type",
    "status",
    "provider_sk",
  ]

  const newObj: any = {}
  for (const key of Object.keys(cObj)) {
    if (strFields.includes(key)) {
      newObj[key] = cObj[key]
    } else {
      const value = cObj[key]
      // Handle NaN, null, undefined, and empty strings
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(value)
      ) {
        newObj[key] = 0
      } else {
        // Parse and round to 2 decimal places for precision
        const parsed = parseFloat(value)
        newObj[key] = isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100
      }
    }
  }
  return newObj
}

const prepareForSave = (cObj: any) => {
  const strFields = [
    "role_category",
    "role_title",
    "resource_name",
    "id",
    // Provider identity fields must remain strings
    "firstname",
    "lastname",
    "provider_type",
    "status",
    "provider_sk",
  ]

  for (const key of Object.keys(cObj)) {
    if (strFields.includes(key)) {
      cObj[key] = cObj[key]
    } else {
      const value = cObj[key]
      // Handle NaN, null, undefined, and empty strings
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(value)
      ) {
        cObj[key] = 0
      } else {
        // Parse, round to 2 decimal places, and convert to number
        const parsed = parseFloat(value)
        cObj[key] = isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100
      }
    }
  }
  return cObj
}

export const updateProvider = (params: any) => {
  const strFields = ["role_category", "role_title"]

  const { field, value, original } = params
  const { aggregations, providers, index } = params

  const _aggregations = prepareForArithmetic(aggregations)
  const _original = prepareForArithmetic(original)
  let updatedEntry: any = prepareForArithmetic(original)
  let updatedAggregation: any = prepareForArithmetic(aggregations)

  if (field === "resource_name") {
    // Value format can be "provider_sk::name" or just "name"
    // Parse to extract provider_sk if present for unique matching
    let providerSk: number | null = null
    let displayName = value

    if (value && value.includes("::")) {
      const parts = value.split("::")
      providerSk = parseInt(parts[0], 10)
      displayName = parts.slice(1).join("::") // In case name has ::
    }

    updatedEntry[field] = displayName

    // Find provider by provider_sk (unique) if available, otherwise fall back to name
    const providerSelected = providers?.find((item: any) => {
      if (providerSk !== null && !isNaN(providerSk)) {
        return item.provider_sk === providerSk
      }
      // Fallback to name matching for backward compatibility
      const name = item["firstname"] + " " + item["lastname"]
      return name?.toLowerCase() === displayName?.toLowerCase()
    })

    updatedEntry = { ...updatedEntry, ...providerSelected }

    // Recalculate dependent fields when provider is selected
    const noOfDays = updatedEntry["no_of_days"] || 0
    const hoursPerDay = updatedEntry["hours_per_day"] || 0
    const amountPerHour = updatedEntry["amount_per_hour"] || 0
    const compensationPercent = updatedEntry["compensation_percent"] || 0
    const preservedCompAmount = updatedEntry["compensation_amount"] || 0

    const hoursPerMonth = roundTo2Decimals(noOfDays * hoursPerDay)
    let production = 0.0

    if (!index) {
      production = roundTo2Decimals(hoursPerMonth * amountPerHour)
      updatedEntry["production"] = production

      // When provider is selected, preserve compensation_percent if present
      if (compensationPercent > 0) {
        // Keep percent, recalc amount from production
        updatedEntry["compensation_percent"] = compensationPercent
        updatedEntry["compensation_amount"] = roundTo2Decimals(
          (production * compensationPercent) / 100,
        )
      } else if (preservedCompAmount > 0) {
        // If only amount present, derive percent
        updatedEntry["compensation_amount"] = preservedCompAmount
        updatedEntry["compensation_percent"] = production
          ? roundTo2Decimals((100 * preservedCompAmount) / production)
          : 0
      } else {
        updatedEntry["compensation_amount"] = 0
        updatedEntry["compensation_percent"] = 0
      }
    }

    updatedEntry["hours_per_month"] = hoursPerMonth

    // Update aggregation for provider selection with proper rounding
    Object.keys(index ? nonClinicalTableFooter : clinicalTableFooter).forEach(
      (key) => {
        updatedAggregation[key] = roundTo2Decimals(
          _aggregations[key] - _original[key] + updatedEntry[key],
        )
      },
    )

    if (!index && updatedAggregation["production"]) {
      updatedAggregation["compensation_percent"] = roundTo2Decimals(
        (updatedAggregation["compensation_amount"] /
          updatedAggregation["production"]) *
        100,
      )
    }
  } else {
    updatedEntry[field] = strFields.includes(field) ? value : parseFloat(value)

    const noOfDays = updatedEntry["no_of_days"] || 0
    const hoursPerDay = updatedEntry["hours_per_day"] || 0
    const amountPerHour = updatedEntry["amount_per_hour"] || 0
    const wagesPerHour = updatedEntry["wages_per_hour"] || 0
    const wagesPerMonth = updatedEntry["wages_per_month"] || 0

    const hoursPerMonth = roundTo2Decimals(noOfDays * hoursPerDay)
    let production = 0.0
    const compAmountField = field === "compensation_amount"
    const compPercentField = field === "compensation_percent"
    const wagesTotalField = field === "wages_per_month"

    // Fields that affect production (when these change, we need to recalculate compensation_percent from preserved compensation_amount)
    const productionAffectingFields = [
      "no_of_days",
      "hours_per_day",
      "amount_per_hour",
    ]
    const productionChanged = productionAffectingFields.includes(field)

    // Always calculate production for clinical hours (doctors/hygienists)
    if (!index) {
      production = roundTo2Decimals(hoursPerMonth * amountPerHour)
      updatedEntry["production"] = production

      // Handle compensation fields based on what was changed
      if (compAmountField) {
        // User changed compensation_amount - calculate compensation_percent from it
        updatedEntry["compensation_percent"] = production
          ? roundTo2Decimals((100 * parseFloat(value)) / production)
          : 0
        updatedEntry["compensation_amount"] = parseFloat(value)
      } else if (compPercentField) {
        // User changed compensation_percent - calculate compensation_amount from it
        updatedEntry["compensation_amount"] = roundTo2Decimals(
          (production * parseFloat(value)) / 100,
        )
        updatedEntry["compensation_percent"] = parseFloat(value)
      } else if (productionChanged) {
        // Production changed - preserve percent, recalc amount
        const preservedCompPercent = updatedEntry["compensation_percent"] || 0
        updatedEntry["compensation_amount"] = roundTo2Decimals(
          (production * preservedCompPercent) / 100,
        )
        updatedEntry["compensation_percent"] = preservedCompPercent
      }
      // If none of the above, preserve both compensation_amount and compensation_percent as-is
    }

    if (wagesTotalField) {
      // User edited wages_per_month - calculate wages_per_hour from it
      updatedEntry["wages_per_hour"] = hoursPerMonth
        ? roundTo2Decimals(wagesPerMonth / hoursPerMonth)
        : 0
    } else if (field === "wages_per_hour") {
      // User edited wages_per_hour - calculate wages_per_month from it
      updatedEntry["wages_per_month"] = roundTo2Decimals(
        hoursPerMonth * wagesPerHour,
      )
    } else {
      // Other fields changed (no_of_days, hours_per_day, etc.) - recalculate wages_per_month
      updatedEntry["wages_per_month"] = roundTo2Decimals(
        hoursPerMonth * wagesPerHour,
      )
    }

    updatedEntry["hours_per_month"] = hoursPerMonth

    // Update aggregation with proper rounding to maintain accuracy
    Object.keys(index ? nonClinicalTableFooter : clinicalTableFooter).forEach(
      (key) => {
        updatedAggregation[key] = roundTo2Decimals(
          _aggregations[key] - _original[key] + updatedEntry[key],
        )
      },
    )

    if (!index && updatedAggregation["production"]) {
      updatedAggregation["compensation_percent"] = roundTo2Decimals(
        (updatedAggregation["compensation_amount"] /
          updatedAggregation["production"]) *
        100,
      )
    }
  }

  updatedEntry = prepareForSave(updatedEntry)
  updatedAggregation = prepareForSave(updatedAggregation)
  return {
    updatedEntry,
    updatedAggregation,
  }
}
